import { useQuery } from '@tanstack/react-query';
import { yasBicimle, type AynaDurumu } from '@raporgor/utils';
import { veriTazeligiApi, type VeriTazeligi } from '../api/veriTazeligi';

/**
 * "Verileriniz ne kadar taze" göstergesi.
 *
 * 🔴 Neden var (canlıda ölçüldü 2026-08-19): Gizova'nın ERP sunucusu 14 Ağustos'ta
 * kapandı, aynası 4,7 gün dondu. Kullanıcı ertesi gün rapor çalıştırdı; baktığı veri
 * beş günlüktü ve ekranda bunu söyleyen HİÇBİR ŞEY yoktu. Tazelik o güne dek yalnız
 * SuperAdmin'e açık bir uçta duruyordu — yani görebilen tek kişi sorunu yaşamayan kişiydi.
 */

/**
 * 🔴 `throwOnError: false` — panelin QueryClient'ı global `throwOnError: true` veriyor.
 * Bu iki bileşen de `Layout`ta, sayfa düzeyindeki hata sınırının DIŞINDA duruyor;
 * uç bir kez 500 dönse hata en yakın sınıra fırlar ve TÜM PANEL beyaz ekrana düşerdi.
 * (Yerlice'de tam bu oldu, S177/A turunda tarayıcıda ölçüldü.)
 *
 * 🔴 Hata durumunda YOKLAMA DA DURUR. `retry: false` bir isteğin denemelerini keser
 * ama sabit `refetchInterval` kalsaydı dakikada bir 500 üretmeye devam ederdi —
 * aynı kusurun seyreltilmişi. Başarı gelirse ritim normale döner.
 */
function useVeriTazeligi() {
  const { data } = useQuery<VeriTazeligi>({
    queryKey: ['veri-tazeligi'],
    queryFn: veriTazeligiApi.get,
    retry: false,
    throwOnError: false,
    refetchInterval: (q) => (q.state.error ? false : 60_000),
    staleTime: 30_000,
  });
  // İzlenmeyen firmada (CDC yok / kurulum bitmemiş) hiçbir şey çizilmez:
  // "veriniz hiç güncellenmemiş" demek yanlış alarm olurdu.
  return data?.izleniyor ? data : null;
}

const ROZET_RENK: Record<AynaDurumu, string> = {
  taze: 'text-ink-500 bg-ink-50 border-border',
  gecikmis: 'text-amber-700 bg-amber-50 border-amber-200',
  bayat: 'text-red-700 bg-red-50 border-red-200',
  hic: 'text-ink-500 bg-ink-50 border-border',
};

function saat(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('tr-TR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Header'ın sağ ucundaki sessiz gösterge. Her durumda görünür (izlenen firmada). */
export function VeriTazeligiRozeti() {
  const t = useVeriTazeligi();
  if (!t) return null;
  return (
    <div
      data-testid="veri-tazeligi-rozet"
      data-durum={t.durum}
      title={`Son güncelleme: ${saat(t.sonSenkronUtc)}`}
      className={`hidden sm:inline-flex items-center gap-1.5 text-[12px] px-2 h-9 rounded-md border ${ROZET_RENK[t.durum]}`}
    >
      <span className="tnum">Veriler {saat(t.sonSenkronUtc)}</span>
    </div>
  );
}

/**
 * Sayfanın en üstündeki uyarı şeridi. YALNIZ gecikmiş/bayat durumda çıkar —
 * taze durumda ekranda hiçbir iz bırakmaz. Rozet tek başına yetmiyordu: Gizova'da
 * kullanıcı beş gün boyunca fark etmedi.
 */
export function VeriTazeligiSeridi() {
  const t = useVeriTazeligi();
  if (!t || (t.durum !== 'bayat' && t.durum !== 'gecikmis')) return null;
  const bayat = t.durum === 'bayat';
  return (
    <div
      data-testid="veri-tazeligi-serit"
      data-durum={t.durum}
      role="status"
      className={`flex items-center gap-2 px-4 py-2 text-[12px] shrink-0 border-b ${
        bayat ? 'bg-red-50 text-red-800 border-red-200' : 'bg-amber-50 text-amber-800 border-amber-200'
      }`}
    >
      <span aria-hidden>⚠</span>
      {/* 🔴 Cümle, süreye EK ALMAYACAK şekilde kuruldu. İlk hâli
          `{yasBicimle(...)}dir güncellenmiyor` idi ve tarayıcıda "4 gün 13 SADIR
          güncellenmiyor" diye basıyordu: `yasBicimle` KISALTMA döndürüyor (sa/dk/sn)
          ve Türkçe eki kısaltmaya yapışmıyor. jsdom bunu göremez — sadece kâğıtta/
          ekranda görünür. "… önce güncellendi" kalıbı ek istemez. */}
      <span>
        <strong>Verileriniz {yasBicimle(t.yasSaniye)} önce güncellendi ({saat(t.sonSenkronUtc)}).</strong>{' '}
        Bu tarihten sonraki hareketler ekranda görünmez.
      </span>
    </div>
  );
}
